import { API_BASE_URL } from './api';
import { authService } from './authService';

export const ReportTargetType = {
  Post: 'Post',
  Comment: 'Comment',
  Event: 'Event',
} as const;
export type ReportTargetTypeValue = typeof ReportTargetType[keyof typeof ReportTargetType];

export const ReportStatus = {
  Open: 'Open',
  Resolved: 'Resolved',
  Dismissed: 'Dismissed',
} as const;
export type ReportStatusValue = typeof ReportStatus[keyof typeof ReportStatus];

export const ReportReason = {
  Spam: 'Spam',
  Harassment: 'Harassment',
  InappropriateContent: 'InappropriateContent',
  Misinformation: 'Misinformation',
  Other: 'Other',
} as const;
export type ReportReasonValue = typeof ReportReason[keyof typeof ReportReason];

export const REPORT_REASON_LABELS: Record<ReportReasonValue, string> = {
  Spam: 'Spam',
  Harassment: 'Harassment or bullying',
  InappropriateContent: 'Inappropriate content',
  Misinformation: 'Misinformation',
  Other: 'Other',
};

export interface Report {
  reportId: string;
  reporterUserId: string;
  reporterDisplayName: string;
  targetType: ReportTargetTypeValue;
  /** Empty for event reports. */
  postId: string;
  commentId?: string | null;
  /** Set for event reports. */
  eventId?: string | null;
  reason: ReportReasonValue;
  details?: string | null;
  targetAuthorUserId: string;
  targetAuthorDisplayName: string;
  targetTextSnapshot?: string | null;
  status: ReportStatusValue;
  createdAt: string;
  resolvedAt?: string | null;
  resolvedByUserId?: string | null;
  actionTaken?: ModerationActionValue | null;
  actionNote?: string | null;
}

export const ModerationAction = {
  RemoveContent: 'RemoveContent',
  WarnUser: 'WarnUser',
  SuspendUser: 'SuspendUser',
} as const;
export type ModerationActionValue = typeof ModerationAction[keyof typeof ModerationAction];

export const MODERATION_ACTION_LABELS: Record<ModerationActionValue, string> = {
  RemoveContent: 'Content removed',
  WarnUser: 'User warned',
  SuspendUser: 'User suspended',
};

export interface UserModerationSummary {
  userId: string;
  displayName: string;
  isSuspended: boolean;
  suspendedUntil?: string | null;
  suspensionReason?: string | null;
  reportsAgainst: Report[];
}

export interface CreateReportRequest {
  targetType: ReportTargetTypeValue;
  /** Required for posts and comments; ignored for events. */
  postId: string;
  commentId?: string;
  /** Required for events. */
  eventId?: string;
  reason: ReportReasonValue;
  details?: string;
}

const authJsonHeaders = () => ({ 'Content-Type': 'application/json', ...authService.getAuthHeaders() });

export const reportService = {
  createReport: async (request: CreateReportRequest): Promise<Report> => {
    const res = await fetch(`${API_BASE_URL}/reports`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify(request),
    });
    if (!res.ok) throw new Error(await res.text() || 'Failed to submit report');
    return res.json();
  },

  // Admin only.
  getReports: async (): Promise<Report[]> => {
    const res = await fetch(`${API_BASE_URL}/reports`, { headers: { ...authService.getAuthHeaders() } });
    if (!res.ok) throw new Error('Failed to load reports');
    return res.json();
  },

  // Admin only.
  resolveReport: async (reportId: string, status: ReportStatusValue): Promise<Report> => {
    const res = await fetch(`${API_BASE_URL}/reports/${encodeURIComponent(reportId)}`, {
      method: 'PATCH',
      headers: authJsonHeaders(),
      body: JSON.stringify({ status }),
    });
    if (!res.ok) throw new Error('Failed to update report');
    return res.json();
  },

  // Admin only. Resolves every open report on the same target as a side effect.
  takeAction: async (reportId: string, action: ModerationActionValue, note?: string, suspendDays?: number): Promise<Report> => {
    const res = await fetch(`${API_BASE_URL}/reports/${encodeURIComponent(reportId)}/actions`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify({ action, note, suspendDays: suspendDays ?? 0 }),
    });
    if (!res.ok) throw new Error(await res.text() || 'Failed to apply action');
    return res.json();
  },

  // Admin only.
  getUserModeration: async (userId: string): Promise<UserModerationSummary> => {
    const res = await fetch(`${API_BASE_URL}/reports/users/${encodeURIComponent(userId)}`, { headers: { ...authService.getAuthHeaders() } });
    if (!res.ok) throw new Error('Failed to load user history');
    return res.json();
  },

  // Admin only.
  liftSuspension: async (userId: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/reports/users/${encodeURIComponent(userId)}/suspension`, {
      method: 'DELETE',
      headers: authJsonHeaders(),
    });
    if (!res.ok) throw new Error('Failed to lift suspension');
  },
};
